import axios from 'axios'
import { miniappSkins } from '@/config/miniappSkins'

export const ADMIN_SKIN_SAVED = 'admin-skin-saved'
export function resolveAdminSkin(value: unknown) {
  return miniappSkins.find(skin => skin.value === value) || miniappSkins[0]
}
export function readPublicSkin(data: any, orgId: number): string | undefined {
  if (Number(data?.orgInfo?.id) !== orgId) return undefined
  try {
    const settings = typeof data.orgInfo.settings === 'string' ? JSON.parse(data.orgInfo.settings) : data.orgInfo.settings
    // 2026-10-25：管理端只读取本端配置；尚未拆分的旧机构沿用原皮肤。
    const theme = settings?.adminTheme === undefined ? settings?.miniappTheme : settings.adminTheme
    return miniappSkins.some(skin => skin.value === theme) ? theme : 'classic'
  } catch (_) { return undefined }
}
export function adminSkinRgb(value:unknown):string {
 const hex=String(value || '').replace(/^#/,'')
 const hsl=/^hsl\(\s*([\d.-]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)$/i.exec(String(value || ''))
 if(hsl){
  const h=((Number(hsl[1])%360)+360)%360/360,s=Number(hsl[2])/100,l=Number(hsl[3])/100
  if(s<=1 && l<=1){const a=s*Math.min(l,1-l);return [0,8,4].map(n=>{const k=(n+h*12)%12;return Math.round(255*(l-a*Math.max(-1,Math.min(k-3,9-k,1))))}).join(',')}
 }
 if(!/^[0-9a-f]{6}$/i.test(hex))return '24,144,255'
 return [0,2,4].map(index=>parseInt(hex.slice(index,index+2),16)).join(',')
}
const pendingColors = ['#64748B','#334155','#F5F6F8','#EEF1F5','#FFFFFF']
let savedSkin: unknown
let personalSkin: string | undefined
export function usePersonalAdminSkin(orgId:number,userId:number) {
  personalSkin=undefined
  if(orgId>0 && userId>0){try{const value=skinStorage()?.getItem(`admin-personal-skin:v1:${orgId}:${userId}`);if(miniappSkins.some(s=>s.value===value))personalSkin=value || undefined}catch(_){/* 默认皮肤继续可用 */}}
  paintAdminSkin(activePreview?.theme ?? personalSkin ?? savedSkin,activePreview?.colors)
  return personalSkin
}
export function selectPersonalAdminSkin(orgId:number,userId:number,value:string|null) {
  if(!(orgId>0 && userId>0) || (value!==null && !miniappSkins.some(s=>s.value===value)))return false
  try{const storage=skinStorage();if(!storage)return false;storage.setItem(`admin-personal-skin:v1:${orgId}:${userId}`,value || "");personalSkin=value || undefined;paintAdminSkin(activePreview?.theme ?? personalSkin ?? savedSkin,activePreview?.colors);return true}catch(_){return false}
}
export function currentAdminSkin(){return resolveAdminSkin(personalSkin ?? savedSkin).value}
let activePreview: {owner:symbol;theme:unknown;colors:string[]} | undefined
function paintAdminSkin(value: unknown, colors?:string[]) {
  const preset = resolveAdminSkin(value)
  const skin = {...preset,colors:colors || (value === 'pending' ? pendingColors : preset.colors)}
  document.documentElement.dataset.adminSkin = skin.value
  document.documentElement.style.setProperty('--activeColorRGB',adminSkinRgb(skin.colors[0]))
  const names = ['--activeColor', '--admin-skin-deep', '--admin-skin-canvas', '--admin-skin-soft', '--admin-skin-surface']
  names.forEach((name, index) => document.documentElement.style.setProperty(name, skin.colors[index]))
  // 仅导航背景加深1.5倍，保留各套皮肤的色相。
  const sidebar = adminSkinRgb(skin.colors[1]).split(',').map(channel => Math.round(Number(channel) / 1.5))
  document.documentElement.style.setProperty('--admin-skin-sidebar', `rgb(${sidebar.join(',')})`)
}
// 编辑期只覆盖当前页面的颜色；公开配置刷新仍记录真实皮肤，离开编辑后恢复。
export function applyAdminSkin(value: unknown) {
  savedSkin=value
  paintAdminSkin(activePreview?.theme ?? personalSkin ?? value,activePreview?.colors)
}
export function createAdminSkinPreview() {
  const owner=Symbol('admin-skin-preview')
  return {
    update(theme:unknown,colors:string[]) {
      activePreview={owner,theme,colors};paintAdminSkin(theme,colors)
    },
    dispose() {
      if(activePreview?.owner!==owner)return
      activePreview=undefined;paintAdminSkin(personalSkin ?? savedSkin)
    }
  }
}
const cachePrefix = 'admin-skin:v1:'
function skinStorage(): Pick<Storage,'getItem'|'setItem'> | undefined {
  try { return typeof window === 'undefined' ? undefined : window.localStorage } catch (_) { return undefined }
}
function cachedSkin(storage: Pick<Storage,'getItem'|'setItem'> | undefined, orgId: number): string | undefined {
  try { const value=storage?.getItem(cachePrefix+orgId);return miniappSkins.some(s=>s.value===value)?value || undefined:undefined } catch (_) { return undefined }
}
// 只读取已有公开皮肤，不调用有编辑权限限制的设置接口，不触发登录拦截器。
export function createAdminSkinSync(load = async (orgId: number) => {
  const result = await axios.get('land/orgidid', { baseURL: process.env.VUE_APP_BASE_API, params: { orgId }, timeout: 8000 })
  if (!result.data?.success) throw new Error('Skin unavailable')
  return result.data.data
}, apply = applyAdminSkin, storage = skinStorage()) {
  let generation = 0, currentOrg = 0
  return {
    async refresh(orgId: number) {
      const revision = ++generation
      // App setup 在首次挂载前执行；只恢复同机构确认过的皮肤，无缓存先用中性色。
      if (orgId !== currentOrg) { currentOrg = orgId; apply(cachedSkin(storage,orgId) || 'pending') }
      try {
        const skin = readPublicSkin(await load(orgId), orgId)
        if (revision === generation && skin) {
          apply(skin)
          try { storage?.setItem(cachePrefix+orgId,skin) } catch (_) { /* 禁用存储不影响皮肤加载。 */ }
        }
      } catch (_) { /* 同店弱网保留当前皮肤，切店失败保留本店缓存或中性色。 */ }
    },
    dispose() { generation++ }
  }
}
